import { correctingTrailingNumbers } from '@cryptoboyio/mercuryo-lab/utils';
import { useTranslation } from 'react-i18next';

import { getFormattedFiatCurrencyNumber } from '@utils';

import { useStore } from '@modules';
import {
  CryptoCurrencyEnum,
  CurrencyConfigType,
  CurrencyEnum,
  FiatCurrencyEnum,
} from '@modules/global/types';
import { IbanStatusesEnum } from '@modules/user/types';
import { GETWalletResponse } from '@modules/wallet/types';

import { DataType, DataKeysEnum } from './types';

export const getDigits = (
  currency: CryptoCurrencyEnum | FiatCurrencyEnum,
  type: 'total_digits' | 'display_digits',
  config: CurrencyConfigType
) => {
  return config.display_options[currency]?.[type];
};

const useData = (selectedCurrency?: CurrencyEnum): DataType => {
  const { t } = useTranslation();
  const [{ cards, wallet, global, accounts, user }] = useStore();
  const { data: walletData } = wallet;
  const { config, crypto } = global.currencies;
  const isCrypto = crypto.some(cryptoCurrency => selectedCurrency === cryptoCurrency);

  const filteredWalletData = crypto.reduce((acc, value) => {
    if (walletData) {
      acc[value as CryptoCurrencyEnum | FiatCurrencyEnum] = Object.values(walletData).find(
        data => data.currency === value
      );
    }

    return acc;
  }, {} as GETWalletResponse);

  const bankTransfer =
    user.data?.iban_status === IbanStatusesEnum.created
      ? [
          {
            key: DataKeysEnum.transfers,
            list: [
              {
                value: 'buy_invoice',
                label: t('iban.top_menu.bank_account'),
                group: DataKeysEnum.transfers,
                inputLabel: t('iban.transfer'),
              },
            ],
          },
        ]
      : [];

  return [
    {
      key: DataKeysEnum.cards,
      list: [
        ...cards.list.map(card => ({
          value: card.id,
          label: `${card.payment_system} · ${card.card_number.slice(-4)}`,
          group: DataKeysEnum.cards,
          inputLabel: t('card.card'),
        })),
        {
          value: 'new_card',
          label: t('card.new_card'),
          group: DataKeysEnum.cards,
          inputLabel: t('card.card'),
          newCard: true,
        },
      ],
    },
    {
      key: DataKeysEnum.my_accounts,
      list: accounts.details.map(account => ({
        value: account.iban,
        label: `${CONST.accountsCurrency} · ${account.iban.slice(-4)}`,
        group: DataKeysEnum.my_accounts,
        inputLabel: getFormattedFiatCurrencyNumber({
          amount: +account.fiat_balance,
          currency: CONST.accountsCurrency,
        }),
      })),
    },
    ...bankTransfer,
    {
      key: DataKeysEnum.my_wallets,
      list: walletData
        ? Object.values(filteredWalletData).map(
            ({ currency, balance, fiat_currency, fiat_balance }) => ({
              value: currency,
              label: currency,
              group: DataKeysEnum.my_wallets,
              inputLabel: isCrypto
                ? `${correctingTrailingNumbers(
                    balance,
                    getDigits(currency, 'display_digits', config),
                    getDigits(currency, 'display_digits', config)
                  )} ${currency}`
                : `${correctingTrailingNumbers(
                    fiat_balance,
                    getDigits(fiat_currency, 'display_digits', config),
                    getDigits(fiat_currency, 'display_digits', config)
                  )} ${fiat_currency}`,
              fullname: config.display_options[currency]?.fullname,
              additionalData: `${correctingTrailingNumbers(
                balance,
                getDigits(currency, 'display_digits', config),
                getDigits(currency, 'display_digits', config)
              )} ${currency} · ${correctingTrailingNumbers(
                fiat_balance,
                getDigits(fiat_currency, 'display_digits', config),
                getDigits(fiat_currency, 'total_digits', config)
              )} ${fiat_currency}`,
            })
          )
        : [],
    },
    {
      key: DataKeysEnum.blockchain_wallets,
      list: walletData
        ? Object.values(walletData).map(({ currency }) => ({
            value: currency,
            label: currency,
            inputLabel: t('header.wallet'),
            group: DataKeysEnum.blockchain_wallets,
            fullname: config.display_options[currency]?.fullname,
          }))
        : [],
    },
  ];
};

export default useData;
